import useUserVarStore from "@app/store/useUserVarStore";
import downArrow from "@assets/actions/down-arrow.svg";
import DefaultImage from "@assets/tm_article_default.svg";
import AiSummary from "@blocks/AiSummary/AiSummary";
import ArticleStickyBar from "@blocks/ArticleStickyBar/ArticleStickyBar";
import { useScrollProgress } from "@blocks/ArticleStickyBar/hooks/useScrollProgress";
import { useStickyVisibility } from "@blocks/ArticleStickyBar/hooks/useStickyVisibility";
import ReactionToolbar from "@blocks/ReactionToolbar/ReactionToolbar";
import ShareSocialButton from "@blocks/ShareSocial/ShareSocialButton";
import AiSummaryModal from "@components/Blocks/AiSummary/AiSummaryModal";
import ArticleCarousel from "@components/Layouts/Carousel/ArticleCarousel";
import ArticleImage from "@components/Layouts/Carousel/ArticleImage";
import RelatedArticleCarousel from "@components/Layouts/Carousel/RelatedArticleCarousel";
import { QnArticleButton } from "@components/Layouts/QuickNews/QnButtons";
import MainBlock from "@container/MainBlock";
import Taboola from "@elements/Advertisement/Taboola";
import ArticleParagraph from "@elements/ArticleParagraph/ArticleParagraph";
import { ArticleGaEventsDataProps } from "@elements/ArticleParagraph/ArticleRegwall";
import GetReadingTime from "@elements/ArticleParagraph/GetReadingTime";
import Author from "@elements/Author/Author";
import BeyondWordsPlayer from "@elements/BeyondWordsPlayer/BeyondWordsPlayer";
import GooglePreferedSourceButton from "@elements/GoogleSourceButton/GooglePreferedSourceButton";
import { ArticleReactionOptions } from "@elements/Reactions/ArticleReactionOptions";
import Date from "@elements/Typography/Date";
import Heading from "@elements/Typography/Heading";
import { StandFirst } from "@elements/Typography/Paragraph";
import { RenderTags } from "@elements/Typography/Tags";
import { shouldShowTTSPlayer } from "@helper/showTTS";
import { formatDate, getDateTime } from "@helper/useDateFormat";
import { getWindowOrigin, isArrayWithElements } from "@helper/utils";
import useArticleReactions from "@hooks/useArticleReactions";
import { useDeviceType } from "@hooks/useDeviceType";
import { useHeaderHeight } from "@hooks/useHeaderHeight";
import { useScrollDirection } from "@hooks/useScrollDirection";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import {
  ArticleMediaCarousel,
  type CarouselImageItem,
  CarouselItemType,
} from "@sphtech/sph-web-ui";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import {
  ArticleAdPageTargetingType,
  ArticleAdSlotTargetingType,
} from "@typings/Ads.d";
import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const socialIconList = ["whatsapp", "facebook", "x", "copylink"];

export type ArticleContentCardProps = {
  /**
   * Processed article data used to render the article content.
   */
  articleData: ProcessedArticleData;

  /**
   * Checks whether the article is rendered as part of infinite scroll.
   */
  isInfiniteScrollArticle?: boolean;

  /**
   * ID of the currently visible article in an infinite-scroll context.
   */
  visibleArticleId?: string;

  /**
   * Infinite scrolled page number of the article.
   */
  pageNumber: number;

  /**
   * Article-level ad targeting values.
   */
  articlePageTarget?: ArticleAdPageTargetingType[];

  /**
   * Article ad slot targeting values.
   */
  articleSlotTarget?: ArticleAdSlotTargetingType[];

  /**
   * Checks Taboola recommendation block should be rendered.
   */
  hasTaboola?: boolean;

  /**
   * Google ad channels used for ad targeting.
   */
  gsChannels?: string[];

  /**
   * Controls whether the article is displayed as a CUE preview.
   */
  isCuePreview?: boolean;

  /**
   * Articles displayed in the trending articles section.
   */
  trendingArticles?: ProcessedArticleData[];

  /**
   * Checks whether the article first-fold design is enabled.
   * @default false
   */
  isArticleFirstFoldEnabled?: boolean;
};

/**
 * Renders the main content of an article, including article metadata,
 * media, authors, reactions, sharing actions, AI summary, article body,
 * related stories, and advertisements.
 *
 * Handles article-specific interactions such as reactions,
 * date editing visibility, text-to-speech playback, article expansion,
 * social sharing, and reading progress tracking.
 *
 * @param props - Configuration and article data required to render the card.
 * @returns The rendered article content card.
 */
export default function ArticleContentCard({
  articleData,
  pageNumber,
  isInfiniteScrollArticle = false,
  visibleArticleId,
  articlePageTarget,
  articleSlotTarget,
  hasTaboola = true,
  gsChannels,
  isCuePreview = false,
  trendingArticles = [],
  isArticleFirstFoldEnabled = false,
}: ArticleContentCardProps): ReactElement {
  const { userToken } = useUserVarStore();
  const {
    id,
    sourceId,
    title,
    sectionName,
    tags,
    authors,
    dateCreated,
    dateEdited,
    kicker,
    articleBody,
    standFirst,
    urlPath,
    imageGallery,
    englishHeadline,
    englishSummary,
    endnote,
    relatedStoriesData,
    datetime,
  } = articleData;

  const {
    reactions,
    userReactions,
    handleReactionClick,
    reactionsError,
    showReactions,
  } = useArticleReactions({
    articleId: id,
    userToken,
    publishDate: datetime,
    uniqueSectionName: sectionName,
  });

  const pageUrl = `${urlPath}`;
  const origin = getWindowOrigin();

  // Get page reading time.
  const readingTime = GetReadingTime(articleBody, urlPath);
  const { isScreenLG } = useWindowSize();
  const [isDateOpen, setDateIsOpen] = useState<boolean>(false);

  // Get count of paragraphs in article body.
  const paraCount = articleBody.filter(
    ({ type }) => type === "paragraph",
  ).length;

  // Toggle the dropdown menu
  const toggleDropdown = () => {
    setDateIsOpen((prev) => !prev);
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const tmCustomGaEventsProps = {
    eventLabel: title,
    articleid: id,
    cue_articleid: id,
    contenttype: "1",
    chapter1: "",
    level2: sectionName,
    pubdate: formatDate(dateCreated, "yyyy-MM-dd HH:mm:ss"),
    title: title,
    url: origin + pageUrl,
    keyword: tags ? tags.map((tags) => tags.name).join(", ") : "",
    pagination: pageNumber,
  } as ArticleGaEventsDataProps;

  const showTTSPlayer = shouldShowTTSPlayer(dateCreated);
  const articleRef = useRef<HTMLDivElement>(null);

  useDeviceType();
  useScrollDirection();
  useStickyVisibility();
  useHeaderHeight();
  const progress = useScrollProgress(articleRef);

  const [isArticleExpanded, setIsArticleExpanded] = useState<boolean>(
    !isInfiniteScrollArticle || paraCount <= 3,
  );

  const filteredAuthors = authors?.filter((author) => author.urlPath?.trim());

  const ttsParentRef = useRef<HTMLDivElement>(null);

  // Update CSS variable for TTS player parent width.
  useEffect(() => {
    if (!isArticleFirstFoldEnabled) return;

    const parent = ttsParentRef.current;

    if (!parent) return;

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      parent.style.setProperty("--standard-width", `${width}px`);
    });

    observer.observe(parent);

    return () => {
      observer.disconnect();
      parent.style.removeProperty("--standard-width");
    };
  }, [isArticleFirstFoldEnabled]);

  const carouselItems: CarouselImageItem[] = useMemo(() => {
    if (!isArrayWithElements(imageGallery)) {
      const fallbackItem: CarouselImageItem = {
        id: "fallback",
        type: CarouselItemType.IMAGE,
        image: {
          url: DefaultImage,
        },
        altText: "Fallback image",
        caption: "",
        credit: "",
      };
      return [fallbackItem];
    }

    return imageGallery.map((item, idx) => {
      return {
        id: item.image?.url ? item.id : `fallback-${idx}`,
        type: CarouselItemType.IMAGE,
        image: {
          url: item.image?.url || DefaultImage,
          width: item.image?.width,
          height: item.image?.height,
        },
        altText: item.altText || item.caption,
        caption: item.caption,
        credit: item.credit,
      };
    });
  }, [imageGallery]);

  return (
    <MainBlock
      showBreadcrumbs={!isInfiniteScrollArticle && !isArticleFirstFoldEnabled}
      sectionName={sectionName}
      pageName={title}
      showTitle={false}
      isArticleFirstFoldEnabled={isArticleFirstFoldEnabled}
    >
      <div ref={articleRef}>
        <ArticleStickyBar
          articleId={id}
          title={title}
          progress={progress}
          socialIconList={socialIconList}
          pageUrl={pageUrl}
          englishHeadline={englishHeadline}
          englishSummary={englishSummary}
          tmCustomGaEventsProps={tmCustomGaEventsProps}
        />
        <article
          className={twMerge("lg:mt-1", isArticleFirstFoldEnabled && "lg:mt-0")}
        >
          <div
            className={twMerge(
              "font-primary font-bold text-2sm leading-430 text-black-400",
              isArticleFirstFoldEnabled &&
                "text-grey-700 text-3sm leading-410 mb-5",
            )}
          >
            {kicker}
          </div>

          <Heading
            className="article-headline text-3md lg:text-lg font-bold font-primary leading-960 text-gray-100"
            sourceId={sourceId}
          >
            {title}
          </Heading>

          {!isArticleFirstFoldEnabled && (
            <>
              <div className="flex flex-col gap-5 lg:gap-0 lg:flex-row justify-between lg:items-center py-5">
                <div className="inline-block">
                  <button
                    onClick={toggleDropdown}
                    className="dropdown-button flex leading-7"
                  >
                    <Date className="mr-2 text-gray-100" dateTime={dateCreated}>
                      {getDateTime(dateCreated)}
                    </Date>
                    {dateEdited > dateCreated && (
                      <img
                        src={downArrow}
                        alt={"dropdown-arrow"}
                        width={10}
                        height={10}
                        className={twMerge(
                          "mt-3 duration-500",
                          isDateOpen && "-rotate-180",
                        )}
                      />
                    )}
                  </button>
                  {isDateOpen && dateEdited > dateCreated && (
                    <>
                      <div className="mb-2 font-primary font-normal text-gray-150 text-xs lg:flex leading-7">
                        <span className="mr-1">மாற்றம் செய்யப்பட்ட நாள்:</span>
                        <Date className="lg:mt-0">
                          {getDateTime(dateEdited)}
                        </Date>
                      </div>
                    </>
                  )}

                  <div className="font-primary font-medium text-gray-150 text-xs">
                    {readingTime} mins read
                  </div>
                </div>

                <div className="flex justify-between lg:justify-center lg:gap-3 items-center">
                  <ReactionToolbar
                    articleId={id}
                    showReactions={showReactions}
                    reactions={reactions}
                    userReactions={userReactions}
                    reactionsError={reactionsError}
                    handleReactionClick={handleReactionClick}
                  />
                  {showReactions && !reactionsError && (
                    <div className="hidden lg:block w-px h-8.4 bg-gray-1050"></div>
                  )}

                  <div className="flex gap-3">
                    <QnArticleButton
                      articleId={id}
                      refCode={"article-content-card"}
                    />
                    <div className="hidden lg:block w-px h-8.4 bg-gray-1050"></div>

                    <ShareSocialButton
                      socialIconList={socialIconList}
                      pagePath={pageUrl}
                      mbPopupClassName="absolute right-0"
                      topPositon={true}
                      buttonTitle=""
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {standFirst && (
            <StandFirst
              className={twMerge(
                isArticleFirstFoldEnabled
                  ? "leading-450 text-3sm mt-0 pt-2.5 mb-5"
                  : "border-t border-solid border-gray-2100 mt-4xs mb-5 pt-xxs",
              )}
            >
              {standFirst}
            </StandFirst>
          )}

          {isArticleFirstFoldEnabled ? (
            <div
              className={twMerge(
                "flex gap-2 mb-5 min-h-12",
                !standFirst && "mt-5",
              )}
              ref={ttsParentRef}
            >
              <div className="flex-grow-no-shrink w-full">
                {showTTSPlayer && (
                  <BeyondWordsPlayer
                    cueId={id}
                    title={title}
                    articleId={id}
                    isInfiniteScrollArticle={isInfiniteScrollArticle}
                    visibleArticleId={visibleArticleId}
                  />
                )}
              </div>
              <ReactionToolbar
                articleId={id}
                showReactions={showReactions}
                reactions={reactions}
                userReactions={userReactions}
                reactionsError={reactionsError}
                handleReactionClick={handleReactionClick}
                enableArticleFirstFold={isArticleFirstFoldEnabled}
              />
            </div>
          ) : (
            showTTSPlayer && (
              <BeyondWordsPlayer
                cueId={id}
                title={title}
                articleId={id}
                isInfiniteScrollArticle={isInfiniteScrollArticle}
                visibleArticleId={visibleArticleId}
              />
            )
          )}

          <div
            data-testid={`article-carousel-wrapper-${id}`}
            className={`article-carousel-wrapper-${id}`}
          >
            {isArticleFirstFoldEnabled ? (
              carouselItems.length === 1 &&
              carouselItems[0].id.startsWith("fallback") ? (
                <ArticleImage
                  url={carouselItems[0].image?.url}
                  alt={carouselItems[0].id}
                  sectionName={sectionName}
                  caption={carouselItems[0].caption}
                  credit={carouselItems[0].credit}
                  className="mb-5"
                />
              ) : (
                <ArticleMediaCarousel items={carouselItems} className="mb-5" />
              )
            ) : (
              isArrayWithElements(imageGallery) && (
                <ArticleCarousel
                  renderItems={imageGallery}
                  sectionName={sectionName}
                />
              )
            )}
          </div>

          {isArrayWithElements(filteredAuthors) ? (
            <div
              className={twMerge(
                isArticleFirstFoldEnabled
                  ? "inline-grid gap-2.5"
                  : "inline-flex",
              )}
            >
              {filteredAuthors.map((author, index) => (
                <Author
                  key={index}
                  destination={author.urlPath ?? ""}
                  authorSrc={author.authorImage?.url ?? ""}
                  authorName={author.name}
                  isArticleFirstFoldEnabled={isArticleFirstFoldEnabled}
                />
              ))}
            </div>
          ) : (
            isArticleFirstFoldEnabled && (
              <div className="inline-block pb-2.5">
                <Date className="mr-2 text-gray-100" dateTime={dateCreated}>
                  {getDateTime(dateCreated)}
                </Date>

                <div className="font-primary font-medium text-gray-150 text-xs">
                  {readingTime} mins read
                </div>
              </div>
            )
          )}
          <div
            className={twMerge(
              "text-end",
              isArrayWithElements(filteredAuthors) && "md:float-end",
              isArticleFirstFoldEnabled && "float-right",
            )}
          >
            <GooglePreferedSourceButton
              className={twMerge(
                "grid md:inline-flex xmd:justify-end w-full md:w-auto pb-3",
                isArticleFirstFoldEnabled && "pb-2.5",
              )}
              pageUrl={pageUrl}
              isArticleFirstFoldEnabled={isArticleFirstFoldEnabled}
            />
          </div>
          {isArticleFirstFoldEnabled ? (
            <div
              className={twMerge(
                "w-full grid gap-2.5 mt-2.5 lg:mt-3.5 lg:gap-3.5",
                !isArrayWithElements(filteredAuthors) &&
                  "mt-0 lg:mt-0 pt-2.5 lg:pt-3.5 border-t border-t-gray-600",
              )}
            >
              {isArrayWithElements(filteredAuthors) && (
                <div className="w-full flex content-center justify-between pb-2.5 border-b border-b-gray-600">
                  <Date className="mr-2 text-gray-100" dateTime={dateCreated}>
                    {getDateTime(dateCreated)}
                  </Date>

                  <div className="font-primary font-medium text-gray-150 text-xs">
                    {readingTime} mins read
                  </div>
                </div>
              )}
              <div className="block h-xl mb-5 w-full">
                <div className="w-1/2 inline-flex">
                  {englishHeadline && englishSummary && (
                    <>
                      <AiSummaryModal
                        englishHeadline={englishHeadline}
                        englishSummary={englishSummary}
                        customGaEvents={tmCustomGaEventsProps}
                        buttonLabel={"Summarise"}
                        pageNumber={pageNumber}
                        sectionName={sectionName}
                        gsChannels={gsChannels}
                      />
                    </>
                  )}
                  <ShareSocialButton
                    socialIconList={socialIconList}
                    pagePath={pageUrl}
                    mbPopupClassName="absolute right-0"
                    topPositon={true}
                    buttonTitle="Share"
                    isArticleFirstFoldEnabled={isArticleFirstFoldEnabled}
                  />
                </div>
                <div className="w-1/2 h-xl content-center float-right lg:px-3">
                  <QnArticleButton
                    articleId={id}
                    refCode={"article-content-card"}
                    isArticleFirstFoldEnabled={isArticleFirstFoldEnabled}
                  />
                </div>
              </div>
            </div>
          ) : (
            englishHeadline &&
            englishSummary && (
              <>
                <AiSummary
                  englishHeadline={englishHeadline}
                  englishSummary={englishSummary}
                  customGaEvents={tmCustomGaEventsProps}
                />
              </>
            )
          )}

          {isArrayWithElements(articleBody) && (
            <ArticleParagraph
              articleId={id}
              articleTitle={title}
              articleBody={articleBody}
              sectionName={sectionName}
              pageUrl={pageUrl}
              dateCreated={dateCreated}
              tags={tags}
              authors={authors}
              pageNumber={pageNumber}
              isInfiniteScrollArticle={isInfiniteScrollArticle}
              articlePageTarget={articlePageTarget}
              articleSlotTarget={articleSlotTarget}
              gsChannels={gsChannels}
              endnote={endnote}
              isCuePreview={isCuePreview}
              trendingArticles={trendingArticles}
              bodyParagraphTotal={paraCount}
              isArticleExpanded={isArticleExpanded}
              setIsArticleExpanded={setIsArticleExpanded}
              isArticleFirstFoldEnabled={isArticleFirstFoldEnabled}
            />
          )}

          {isArticleExpanded && (
            <div className="flex flex-col lg:flex-row gap-5 lg:gap-0 justify-between lg:items-center mt-5 mb-10 lg:my-10">
              <div className="flex flex-col gap-2">
                <div className="text-gray-100 font-semibold font-sm leading-[140%] hidden lg:block">
                  இச்செய்தியைப் பகிரவும்
                </div>
                <ShareSocialButton
                  socialIconList={socialIconList}
                  pagePath={pageUrl}
                  mbPopupClassName="mb-5"
                  deskClassName="my-2"
                />
              </div>
              <div>
                {showReactions && reactions.length != 0 && (
                  <ArticleReactionOptions
                    reactions={reactions}
                    handleReactionClick={handleReactionClick}
                    reactionsUser={userReactions}
                    articleId={id}
                    variant="bottom"
                  />
                )}
              </div>
            </div>
          )}

          {/* Tags dont dont render If alias not available in payload*/}
          {isArrayWithElements(tags) && isArticleExpanded && (
            <RenderTags tagsWrapper="mb-5 lg:mb-6" tags={tags} />
          )}

          {relatedStoriesData && isArrayWithElements(relatedStoriesData) && (
            <RelatedArticleCarousel
              relatedArticles={relatedStoriesData}
              rootClassName="track-impression mt-6 overflow-hidden"
              testId="related-article-carousel"
              dataSectionName="related-story-bottom"
              source={"related-bottom"}
            />
          )}
        </article>

        {/* Video block */}
        {/* {isScreenLG && !isInfiniteScrollArticle && <LatestVideo />} */}

        {/* Taboola block */}
        {hasTaboola && isScreenLG && (
          <div className="border-t border-pink-300 pt-6">
            <Taboola
              counter={pageNumber}
              pathName={pageUrl}
              pageType="article"
              mode="thumbnails-base-widget"
              container="below-article-thumbnails"
              placement="Below Article Thumbnails"
              targetType="mix"
              isFlush={true}
            />
          </div>
        )}
      </div>
    </MainBlock>
  );
}
